import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FileText, XCircle } from 'lucide-react';
import type { PaymentSubmissionQueueRow, PaymentSubmissionStatus } from '@dru/types';
import { PAYMENT_METHOD_LABEL, formatPaise } from '@dru/types';
import { Alert, Button, Checkbox, DataTable, Field, IconAction, KeyFigures, RowActions, SearchInput, StatusBadge, Tabs, Textarea, useToast } from '@dru/ui';
import { Dialog } from '../../../components/Dialog';
import {
  ReferenceReuseAlert, SUBMISSION_LABEL, SUBMISSION_TONE, fileSize, liveUses, openProof,
} from '../../../components/PaymentSubmissions';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDate, formatDateTime } from '../../../lib/format';

type Decision = { row: PaymentSubmissionQueueRow; kind: 'confirm' | 'reject' };

const EMPTY: Record<PaymentSubmissionStatus, { title: string; hint: string }> = {
  PENDING: {
    title: 'Nothing waiting for verification',
    hint: 'When a company submits a payment against an invoice, it appears here with its proof for you to check against the bank statement.',
  },
  CONFIRMED: { title: 'No confirmed submissions', hint: 'Submissions you confirm are listed here with the date they were confirmed.' },
  REJECTED: { title: 'No rejected submissions', hint: 'Submissions you reject are listed here with the reason the company was given.' },
  CANCELLED: { title: 'No withdrawn submissions', hint: 'Submissions a company withdrew before they were decided are listed here.' },
};

/**
 * Dr Ultimate's review of what companies say they have paid (docs/37 A1,
 * Step 6).
 *
 * Every pending submission across every company, oldest first, with the
 * invoice's confirmed outstanding beside the amount claimed. Confirming
 * writes the ledger payment and is what moves paid and outstanding;
 * rejecting needs a reason the company can act on, and writes nothing.
 *
 * Each row answers the four questions the reviewer has, in reading order:
 * who says they paid, against which invoice, how much, and with what
 * evidence — and the two decisions are drawn as different acts.
 *
 * A reference the company's account already carries — on another submission,
 * or on a payment recorded straight onto the ledger — is said on the row and
 * again in both decision dialogs, with the other invoice, date and standing.
 * It is a warning, not a refusal: one transfer can pay several invoices. One
 * UTR had been accepted as proof on three payments and a fourth submission
 * with nothing here to show it (audit of 1 Oct 2026).
 */
export function PaymentReview() {
  const { permissions } = useAuth();
  const [status, setStatus] = useState<PaymentSubmissionStatus>('PENDING');
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  // The pending queue is always read: its count rides on its tab whichever
  // tab is open. The other states are read only when their tab is.
  const pendingQueue = useQuery<{ items: PaymentSubmissionQueueRow[] }>(
    () => api.get('/payment-submissions?status=PENDING&limit=200'), [],
  );
  const decided = useQuery<{ items: PaymentSubmissionQueueRow[] } | null>(
    () => (status === 'PENDING' ? Promise.resolve(null) : api.get(`/payment-submissions?status=${status}&limit=200`)), [status],
  );
  const queue = status === 'PENDING' ? pendingQueue : decided;
  const canDecide = permissions.has('invoice.payment-confirm');
  const loaded = queue.data?.items ?? [];
  // Narrows what is loaded (at most 200 per state): company, plant, invoice
  // number or bank reference — the four things a reviewer has in hand.
  const term = q.trim().toLowerCase();
  const rows = term
    ? loaded.filter((r) => `${r.tenant.name} ${r.plant.code} ${r.plant.name} ${r.invoice.invoiceNumber} ${r.reference}`.toLowerCase().includes(term))
    : loaded;
  const pendingRows = pendingQueue.data?.items ?? [];
  const pendingTotal = pendingRows.reduce((n, r) => n + r.amountPaise, 0);
  const companies = new Set(pendingRows.map((r) => r.tenantId)).size;
  const oldest = pendingRows[0]?.submittedAt;
  const proof = (r: PaymentSubmissionQueueRow) => openProof(r.id, r.tenantId).catch((e) => setError(errorMessage(e)));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payment review"
        hint="Payments companies say they have made. Check each against the bank statement and its proof, then confirm it (it is recorded as paid) or reject it (nothing is recorded)."
      />
      {status === 'PENDING' && pendingRows.length > 0 && (
        <KeyFigures
          label="Waiting for verification"
          figures={[
            { key: 'n', label: 'Submissions to verify', value: String(pendingRows.length), tone: 'pending' },
            { key: 'amt', label: 'Amount claimed', value: formatPaise(pendingTotal), hint: 'Not counted as paid until confirmed' },
            { key: 'co', label: 'Companies', value: String(companies) },
            { key: 'old', label: 'Oldest submitted', value: formatDate(oldest), hint: oldest ? formatDateTime(oldest).split(', ')[1] : undefined },
          ]}
        />
      )}
      <Tabs
        tabs={(['PENDING', 'CONFIRMED', 'REJECTED', 'CANCELLED'] as const).map((s) => ({
          key: s,
          label: SUBMISSION_LABEL[s],
          ...(s === 'PENDING' && pendingRows.length ? { hint: pendingRows.length } : {}),
        }))}
        active={status}
        onChange={(k) => setStatus(k as PaymentSubmissionStatus)}
      />
      {loaded.length > 0 && (
        <SearchInput
          value={q} onValueChange={setQ} className="w-full sm:max-w-sm"
          placeholder="Search company, invoice or UTR"
          aria-label="Search submissions by company, invoice number or payment reference"
        />
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      {loaded.length >= 200 && (
        <Alert tone="info">
          Showing the oldest 200 submissions in this state. Confirm or reject some to see the rest.
        </Alert>
      )}
      <DataTable<PaymentSubmissionQueueRow>
        rows={rows}
        loading={queue.loading}
        error={queue.error}
        onRetry={queue.reload}
        rowKey={(r) => r.id}
        empty={term && loaded.length ? {
          title: 'No submission matches',
          hint: 'Nothing in this list matches that company, invoice or reference.',
          action: <Button variant="secondary" onClick={() => setQ('')}>Clear search</Button>,
        } : EMPTY[status]}
        columns={[
          { key: 'who', header: 'From', alwaysVisible: true, cell: (r) => (
            <div className="min-w-[10rem] max-w-[16rem]">
              <div className="truncate font-medium text-neutral-900" title={r.tenant.name}>{r.tenant.name}</div>
              <div className="truncate text-caption text-neutral-500" title={r.plant.name}>Plant {r.plant.code} · {r.plant.name}</div>
              <div className="text-caption text-neutral-500">
                by {r.submittedByName ?? 'the company'} · {formatDateTime(r.submittedAt)}
              </div>
            </div>
          ) },
          { key: 'invoice', header: 'Invoice', cell: (r) => (
            <div className="whitespace-nowrap">
              <Link to={`/admin/invoices/${r.invoice.id}?tenantId=${r.tenantId}`} className="font-mono text-small text-primary-600 hover:text-primary-700">
                {r.invoice.invoiceNumber}
              </Link>
              <div className="text-caption tabular-nums text-neutral-500">
                {formatPaise(r.outstandingPaise, r.invoice.currency)} outstanding
              </div>
              {r.status === 'PENDING' && r.pendingPaise > r.amountPaise && (
                <div className="text-caption tabular-nums text-pending-600">
                  {formatPaise(r.pendingPaise - r.amountPaise, r.invoice.currency)} more pending
                </div>
              )}
            </div>
          ) },
          { key: 'amount', header: 'Amount claimed', align: 'right', cell: (r) => (
            <div className="whitespace-nowrap">
              <div className="tabular-nums font-semibold text-neutral-900">{formatPaise(r.amountPaise, r.invoice.currency)}</div>
              {r.status === 'PENDING' && r.amountPaise > r.outstandingPaise && (
                <div className="text-caption text-warning-600">More than outstanding</div>
              )}
            </div>
          ) },
          { key: 'evidence', header: 'Payment and proof', cell: (r) => (
            <div className="text-small">
              <div className="whitespace-nowrap text-neutral-800">{formatDate(r.paidOn)} · {PAYMENT_METHOD_LABEL[r.method] ?? r.method}</div>
              <div className="font-mono text-neutral-700">{r.reference}</div>
              <ReferenceReused row={r} />
              <Button
                size="sm" variant="ghost" className="-ml-3 mt-0.5"
                icon={<FileText className="h-4 w-4" aria-hidden />}
                aria-label={`View proof ${r.proofFileName} from ${r.tenant.name}`}
                onClick={(e) => { e.stopPropagation(); proof(r); }}
              >
                <span className="max-w-[10rem] truncate">{r.proofFileName}</span>
                <span className="font-normal text-neutral-500">{fileSize(r.proofSizeBytes)}</span>
              </Button>
            </div>
          ) },
          // Every row on the pending tab is pending; the column earns its
          // place only where the outcome differs.
          ...(status === 'PENDING' ? [] : [{
            key: 'status', header: 'Outcome', mobileLabel: false as const,
            cell: (r: PaymentSubmissionQueueRow) => <Outcome row={r} />,
          }]),
          ...(status === 'PENDING' && canDecide ? [{
            key: 'act', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '' as const,
            cell: (r: PaymentSubmissionQueueRow) => (
              <RowActions>
                <IconAction
                  label={`Reject payment ${r.reference} for ${r.invoice.invoiceNumber}`}
                  icon={XCircle} danger
                  onClick={() => setDeciding({ row: r, kind: 'reject' })}
                />
                <IconAction
                  label={`Confirm payment ${r.reference} for ${r.invoice.invoiceNumber}`}
                  icon={CheckCircle2} primary
                  onClick={() => setDeciding({ row: r, kind: 'confirm' })}
                />
              </RowActions>
            ),
          }] : []),
        ]}
      />
      {deciding && (
        <DecisionDialog
          decision={deciding}
          onProof={() => proof(deciding.row)}
          onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); pendingQueue.reload(); }}
        />
      )}
    </div>
  );
}

const invoiceHref = (tenantId: string) => (u: { invoiceId: string }) => `/admin/invoices/${u.invoiceId}?tenantId=${tenantId}`;

/**
 * On the row: that the reference stands elsewhere, and on which invoices.
 * The dates and amounts are in the decision dialog; the row only has to stop
 * the eye before Confirm is pressed.
 */
function ReferenceReused({ row: r }: { row: PaymentSubmissionQueueRow }) {
  const live = liveUses(r.referenceUses);
  if (live.length === 0) return null;
  const shown = live.slice(0, 2);
  return (
    <div className="mt-0.5 max-w-[16rem] rounded-badge bg-warning-100 px-1.5 py-0.5 text-caption font-medium text-warning-600">
      Reference already used: {shown.map((u, i) => (
        <span key={`${u.kind}:${u.id}`}>
          {i > 0 ? ', ' : ''}
          <Link to={invoiceHref(r.tenantId)(u)} onClick={(e) => e.stopPropagation()} className="font-mono underline">{u.invoiceNumber}</Link>
        </span>
      ))}
      {live.length > shown.length ? ` and ${live.length - shown.length} more` : ''}
    </div>
  );
}

/** How a decided submission ended, when, and why if it was rejected. */
function Outcome({ row: r }: { row: PaymentSubmissionQueueRow }) {
  return (
    <div className="text-small">
      <StatusBadge tone={SUBMISSION_TONE[r.status]}>{SUBMISSION_LABEL[r.status]}</StatusBadge>
      {r.reviewedAt && (
        <div className="mt-1 text-caption text-neutral-500">
          {formatDateTime(r.reviewedAt)}{r.reviewedByName ? ` · ${r.reviewedByName}` : ''}
        </div>
      )}
      {r.rejectionReason && <div className="mt-1 max-w-[16rem] text-caption text-neutral-600">{r.rejectionReason}</div>}
    </div>
  );
}

function DecisionDialog({ decision: { row, kind }, onProof, onClose, onDone }: {
  decision: Decision; onProof: () => void; onClose: () => void; onDone: () => void;
}) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  // Confirming is a statement about the bank account, not about the proof
  // file: the reviewer says so in a tick, so Confirm is never one stray click.
  const [matched, setMatched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per decision: a double click is one request, and the API answers
  // a repeated confirmation with the payment the first one made regardless.
  const idempotencyKey = useRef(crypto.randomUUID());
  const money = (p: number) => formatPaise(p, row.invoice.currency);
  const overpays = row.amountPaise > row.outstandingPaise;
  const reasonShort = reason.trim().length < 10;

  // A double click must send one request, not two with the same key.
  const inFlight = useRef(false);

  async function decide() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    try {
      await api.post(
        `/payment-submissions/${row.id}/${kind}?tenantId=${row.tenantId}`,
        kind === 'reject' ? { reason: reason.trim() } : {},
        idempotencyKey.current,
      );
      toast('success', kind === 'confirm' ? `Payment of ${money(row.amountPaise)} confirmed and recorded` : 'Submission rejected — the company has been told why');
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { inFlight.current = false; setBusy(false); }
  }

  return (
    <Dialog
      title={kind === 'confirm' ? `Confirm ${money(row.amountPaise)} as received?` : 'Reject this payment submission?'}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          {kind === 'confirm'
            ? <Button variant="success" onClick={decide} loading={busy} disabled={overpays || !matched}>Confirm payment</Button>
            : <Button variant="danger" onClick={decide} loading={busy} disabled={reasonShort}>Reject submission</Button>}
        </>
      )}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small">
        <dt className="text-neutral-500">Company</dt><dd className="text-neutral-900">{row.tenant.name}</dd>
        <dt className="text-neutral-500">Plant</dt><dd>{row.plant.code} · {row.plant.name}</dd>
        <dt className="text-neutral-500">Invoice</dt><dd className="font-mono">{row.invoice.invoiceNumber}</dd>
        <dt className="text-neutral-500">Amount claimed</dt><dd className="font-semibold tabular-nums text-neutral-900">{money(row.amountPaise)}</dd>
        <dt className="text-neutral-500">Paid on</dt><dd>{formatDate(row.paidOn)} · {PAYMENT_METHOD_LABEL[row.method] ?? row.method}</dd>
        <dt className="text-neutral-500">UTR / reference</dt><dd className="font-mono">{row.reference}</dd>
        <dt className="text-neutral-500">Outstanding now</dt><dd className="tabular-nums">{money(row.outstandingPaise)}</dd>
      </dl>
      <Button size="sm" variant="secondary" className="mt-2" icon={<FileText className="h-4 w-4" aria-hidden />} onClick={onProof}>
        View proof ({row.proofFileName})
      </Button>
      <ReferenceReuseAlert
        className="mt-3" reference={row.reference} uses={row.referenceUses} currency={row.invoice.currency}
        amountPaise={row.amountPaise} invoiceHref={invoiceHref(row.tenantId)}
      >
        {kind === 'confirm'
          ? 'One bank transfer can pay several invoices. Confirm only if the credit on the statement covers every payment listed against this reference; otherwise reject with the reason.'
          : 'If the same credit has already been counted against those invoices and does not cover this one too, say so in the reason.'}
      </ReferenceReuseAlert>
      {kind === 'confirm' ? (
        overpays
          ? <Alert tone="warning" className="mt-3">This is more than is outstanding now — the invoice has been paid some other way since. Reject it with the reason instead.</Alert>
          : (
            <div className="mt-3 space-y-2 text-small text-neutral-700">
              <p>
                Confirm only once the amount and reference match your bank statement. Confirming records {money(row.amountPaise)} on
                the invoice as received: outstanding goes from {money(row.outstandingPaise)} to {money(row.outstandingPaise - row.amountPaise)},
                and the company sees it as paid.
              </p>
              <Alert tone="warning">
                A confirmed payment cannot be edited or un-confirmed. A mistake is corrected by recording a reversal on the company’s billing.
              </Alert>
              <Checkbox
                label={`I have found a credit of ${money(row.amountPaise)} with reference ${row.reference} on the bank statement`}
                checked={matched} onChange={(e) => setMatched(e.target.checked)}
              />
            </div>
          )
      ) : (
        <div className="mt-3 space-y-3">
          <p className="text-small text-neutral-700">
            Nothing is recorded on the invoice; it stays at {money(row.outstandingPaise)} outstanding. The company sees your reason and can submit a corrected payment.
          </p>
          <Field
            label="Reason (shown to the company)"
            hint="At least 10 characters."
            info="For example, “No credit for this UTR on the statement for 24 Sep”."
            required
          >
            {(p) => <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" rows={3} />}
          </Field>
        </div>
      )}
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}
